import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
import SearchBar from "../../SearchBarTable/SearchBarTable";
import SortHeader from "../SortingHeader";
import { FaCubes } from "react-icons/fa";
// @ts-ignore CSS is loaded by the application bundler.
import "../AgGridTheme.css";

import {
  ClientSideRowModelModule,
  TextFilterModule,
  PaginationModule,
  ModuleRegistry,
} from "ag-grid-community";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  PaginationModule,
]);

interface PIScreenTableProps {
  records: any[];
  selectedRow: number | null;
  onRowSelect: (record: any) => void;
  onClearSelection: () => void;
  searchPrefill?: string | null;
  onSearchUsed?: () => void;
}

const PIScreenTable: React.FC<PIScreenTableProps> = ({
  records,
  selectedRow,
  onRowSelect,
  searchPrefill,
  onSearchUsed,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef<AgGridReact>(null);

  const filteredRecords = useMemo(() => {
    const s = searchQuery.toLowerCase();

    return records.filter((r) => {
      return (
        r.ISO_RESOURCE_NAME?.toLowerCase().includes(s) ||
        r.METRIC_NAME?.toLowerCase().includes(s) ||
        r.DATA_POINT_NAME?.toLowerCase().includes(s) ||
        r.METRIC_INTERNAL_KEY?.toLowerCase().includes(s) ||
        r.SUMMARY_TYPE?.toLowerCase().includes(s)
      );
    });
  }, [records, searchQuery]);

  useEffect(() => {
    if (searchPrefill) {
      setSearchQuery(searchPrefill);
    }
  }, [searchPrefill]);
  
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Resource Name",
        field: "ISO_RESOURCE_NAME",
        flex: 1.2,
        minWidth: 220,
        sortable: true,
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Resource Name",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      },
      {
        headerName: "Metric Name",
        field: "METRIC_NAME",
        flex: 1,
        minWidth: 180,
        sortable: true,
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Metric Name",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      },
      {
        headerName: "PI Element",
        field: "DATA_POINT_NAME",
        flex: 1.5,
        minWidth: 300,
        sortable: true,
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "PI Element",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      },
      {
        headerName: "Summary Type",
        field: "SUMMARY_TYPE",
        flex: 1,
        minWidth: 180,
        sortable: true,
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Summary Type",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      },
    ],
    []
  );
  

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      cellStyle: {
        padding: "8px 12px",
        fontSize: "16px",
        fontFamily: "'Poppins', sans-serif",
        color: "#374151",
      },
    }),
    []
  );

  const handleRowClicked = useCallback(
    (event: any) => {
      onRowSelect(event.data);
    },
    [onRowSelect]
  );

  return (
    <div className="p-4 bg-white shadow-md rounded-xl font-poppins">
      {/* SEARCH */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full max-w-md">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={(value) => {
              setSearchQuery(value);
              onSearchUsed?.();
            }}
            placeholder="Search PI metrics..."
          />
        </div>
      </div>

      {/* TABLE */}
      <div
        className="ag-theme-alpine font-poppins text-base text-gray-700"
        style={{
          height: "var(--admin-tab-grid-height, max(475px, calc(100dvh - 320px)))",
          width: "100%",
          border: "1px solid #E7E4FF",
          borderRadius: "8px",
        }}
      >
        <AgGridReact
          theme="legacy"
          ref={gridRef}
          rowData={filteredRecords}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={{ mode: 'singleRow' }}
          rowHeight={60}
          headerHeight={55}
          pagination
          paginationPageSize={15}
          paginationPageSizeSelector={[15, 20, 50, 100]}
          animateRows
          onRowClicked={handleRowClicked}
          getRowClass={(params) =>
            params.data.PI_METRIC_ID === selectedRow ? "bg-gray-200" : ""
          }
        />
      </div>
    </div>
  );
};

export default PIScreenTable;
